import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Alert from '../utils/Alert';
import cx from 'classnames';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { AxiosGet, AxiosPost } from '../http/Axios';

const ConfirmRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [invalidId, setInvalidId] = useState(false);
  const [error, setError] = useState('');
  const queryParams = new URLSearchParams(location.search);
  const guid = queryParams.get('guid');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    const checkGuid = async () => {
      if (!guid) {
        setInvalidId(true);
        return;
      }
      try {
        const data = await AxiosGet(`/kidlab-reg/families/confirm?guid=${guid}`);
        if (!data || data.valid !== true) {
          setInvalidId(true);
        }
      } catch (error) {
        console.error('Error validating GUID:', error);
        setInvalidId(true);
      }
    };
    checkGuid();
  }, [guid]);

  const confirmData = async (formValues) => {
    setIsSubmitting(true);
    setError('');

    try {
      if (!executeRecaptcha) {
        setError('reCAPTCHA not ready, please try again.');
        setIsSubmitting(false);
        return;
      }

      const token = await executeRecaptcha('confirm_registration');

      const response = await AxiosPost(`/kidlab-reg/families/confirmAndRegister?guid=${guid}`, {
        email: formValues.email,
        zipCode: formValues.zipCode,
        recaptchaToken: token,
      });

      if (response && response.message) {
        navigate('/registration-confirmation');
      } else {
        setError(response?.message || 'Unexpected server response. Please try again.');
      }
    } catch (err) {
      console.error('Error confirming registration:', err);
      const message = err?.response?.data?.message || err.message || 'An error occurred.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-page page-container">
      <div className="page-title">
        <h1>LeapFrog Lab Registration</h1>
      </div>
      {invalidId && (
        <div className="registration-form form-group">
          <h2 className="page-subtitle">
            The account you are attempting to access is no longer valid.
          </h2>
          <p>
            If you received an email requesting your confirmation, please follow
            the instructions in the email to access this feature.
          </p>
        </div>
      )}
      {error && <Alert message={error} />}
      {!invalidId && (
        <div className="registration-form form-group">
          <h2 className="page-subtitle">Please confirm your registration</h2>
          <form onSubmit={handleSubmit(confirmData)}>
            <p>
              To finalize your registration with LeapFrog's Lab, please enter
              your email address and your home zip code below:
            </p>
            <div className={cx('field-group', { 'validation-errors': errors.email })}>
              <label htmlFor="email">Confirm email address</label>
              <input
                type="email"
                {...register('email', {
                  required: true,
                  pattern: /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                  maxLength: 99,
                })}
              />
              {errors.email && (
                <span className="error-label">
                  Email should contain valid characters and format.
                </span>
              )}
            </div>

            <div className={cx('field-group', { 'validation-errors': errors.zipCode })}>
              <label htmlFor="zipCode">Confirm zip code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                {...register('zipCode', {
                  required: true,
                  pattern: /^\d{5}$/,
                  maxLength: 5,
                })}
                placeholder="#####"
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, '').slice(0, 5);
                }}
              />
              {errors.zipCode && (
                <span className="error-label">Zip Code should contain 5 digits only.</span>
              )}
            </div>

            <div className="field-group button">
              <button className="button primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Continue'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ConfirmRegistration;
